import React, { useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import CustomText from "../../../common/general/CustomText/CustomText";
import { colors, radii, spacing, typographyScale } from "../../../../styles/design-system";

type ColorVariant = "default" | "mcq";

const variantStyles: Record<
  ColorVariant,
  {
    cardBg: string;
    cardBorder: string;
    cardPadding: number;
    baseBorder: string;
    baseBg: string;
    hoverBorder: string;
    selectedBorder: string;
    selectedBg: string;
    labelColor: string;
  }
> = {
  default: {
    cardBg: "transparent",
    cardBorder: "transparent",
    cardPadding: spacing.none,
    baseBorder: colors.grey[200],
    baseBg: colors.white,
    hoverBorder: colors.grey[300],
    // Neutral “your choice” — white row + border only (no success styling)
    selectedBorder: colors.grey[300],
    selectedBg: colors.white,
    labelColor: colors.grey[600],
  },
  mcq: {
    cardBg: colors.accent.purple50,
    cardBorder: colors.accent.purple200,
    cardPadding: spacing.sm,
    baseBorder: colors.grey[200],
    baseBg: colors.white,
    hoverBorder: colors.accent.purple300,
    selectedBorder: colors.accent.purple300,
    selectedBg: colors.white,
    labelColor: colors.accent.purple700,
  },
};

export interface InlineResponseCardProps {
  question: string;
  choices: string[];
  selectedChoiceIndex: number | null;
  submitted: boolean;
  onSelectChoice?: (index: number) => void;
  onSubmit?: () => void;
  colorVariant?: ColorVariant;
}

export const InlineResponseCard = ({
  question,
  choices,
  selectedChoiceIndex,
  submitted,
  onSelectChoice,
  onSubmit,
  colorVariant = "default",
}: InlineResponseCardProps) => {
  const selectedStyles = variantStyles[colorVariant];
  const [hoveredChoiceIndex, setHoveredChoiceIndex] = useState<number | null>(null);

  return (
    <View
      style={[
        styles.card,
        {
          backgroundColor: selectedStyles.cardBg,
          borderColor: selectedStyles.cardBorder,
          padding: selectedStyles.cardPadding,
        },
      ]}>
      <View style={styles.questionHeader}>
        {colorVariant !== "default" && (
          <CustomText
            useTranslationText={false}
            text="Check your understanding"
            size="s"
            weight="medium"
            style={[styles.questionLabel, { color: selectedStyles.labelColor }]}
          />
        )}
        <CustomText useTranslationText={false} text={question} size="s" weight="normal" style={styles.questionText} />
      </View>

      <View style={styles.choiceList}>
        {choices.map((choice, index) => {
          const isSelected = selectedChoiceIndex === index;
          const isDimmed = submitted && !isSelected;
          return (
            <Pressable
              key={index}
              disabled={submitted}
              onPress={() => {
                onSelectChoice?.(index);
                onSubmit?.();
              }}
              onHoverIn={() => setHoveredChoiceIndex(index)}
              onHoverOut={() => setHoveredChoiceIndex((prev) => (prev === index ? null : prev))}
              style={({ pressed }) => [
                styles.choiceRow,
                {
                  borderColor: selectedStyles.baseBorder,
                  backgroundColor: selectedStyles.baseBg,
                },
                hoveredChoiceIndex === index && !submitted && { borderColor: selectedStyles.hoverBorder },
                pressed && !submitted && styles.choiceRowPressed,
                isSelected && {
                  borderColor: selectedStyles.selectedBorder,
                  backgroundColor: selectedStyles.selectedBg,
                },
                isDimmed && styles.choiceRowDimmed,
              ]}
              accessibilityRole="radio"
              accessibilityState={{ selected: isSelected, disabled: submitted }}
              accessibilityHint={submitted && isSelected ? "Your selected response" : undefined}>
              <CustomText
                useTranslationText={false}
                text={choice}
                size="s"
                weight="normal"
                style={[styles.choiceText, isDimmed && styles.choiceTextDimmed]}
              />
            </Pressable>
          );
        })}
      </View>
    </View>
  );
};

// Reference: ChallengeContainer max-w-2xl, rounded-xl = tokens.radius.xl (32px), p-5 (20px).
// InlineResponseOptions: rounded-lg = tokens.radius.lg (16px), text-sm (14px), gap-2 (8px), max-w-[480px].
const CARD_MAX_WIDTH = 672;
const OPTIONS_MAX_WIDTH = 480;

const styles = StyleSheet.create({
  card: {
    width: "100%",
    maxWidth: CARD_MAX_WIDTH,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderRadius: radii.xl,
  },
  questionHeader: {
    marginBottom: 0,
  },
  questionLabel: {
    letterSpacing: 0.8,
    marginBottom: spacing.xs,
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  questionText: {
    color: colors.grey[800],
    fontSize: typographyScale.textBase.fontSize,
    lineHeight: typographyScale.textBase.lineHeight,
  },
  choiceList: {
    gap: spacing.xs,
    maxWidth: OPTIONS_MAX_WIDTH,
    width: "100%",
    alignSelf: "flex-start",
    marginTop: spacing.sm,
  },
  choiceRow: {
    borderWidth: 1,
    borderRadius: radii.lg,
    minHeight: 44,
    width: "100%",
    justifyContent: "flex-start",
    paddingHorizontal: spacing.sm,
    paddingVertical: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  choiceRowPressed: {
    transform: [{ scale: 0.98 }],
  },
  choiceRowDimmed: {
    borderColor: colors.grey[100],
    backgroundColor: "transparent",
  },
  choiceText: {
    color: colors.grey[900],
    flex: 1,
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  choiceTextDimmed: {
    color: colors.grey[400],
  },
});
